import {useRouter, useNavigate as useTanStackNavigate} from '@tanstack/react-router';
import queryString from 'query-string';
import {
  DependencyList,
  EffectCallback,
  RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {useHotkeys} from 'react-hotkeys-hook';
import {HotkeyCallback} from 'react-hotkeys-hook/dist/types';
import {useIsFirstRender} from 'usehooks-ts';

import {NAVIGATION_MAP} from '~constants/constants';
import {useCurrentProjectQuery} from '~api/projects.queries';
import {getDeviceAlias} from '~utils/deviceUtils';
import {createDebouncer, hasOpenedDialogs, isInternalEmail, isProductionEnv} from '~utils/miscUtils';
import {
  confusionMatrixCellIdQueryParam,
  evaluationCellPredictedClassQueryParam,
  getRoute,
  keypointFinetuneQueryParam,
  queryStringParseOptions,
  returnPathQueryParam,
  Route,
  similaritySearchQueryParam,
  useQueryParam,
  viewPipelineIdQueryParam,
} from '~utils/routeUtil';
import {useIsUnstableGallery} from '~hooks/useIsUnstableGallery';
import {Dimensions, ImageOrderBy} from '~redux/types/images';
import {Device, UserWithId} from '~redux/types/user';
import {selectPerspectives} from '~redux/reducers/filtersReducer';
import {selectUser} from '~redux/reducers/userReducer';
import {useAppDispatch, useAppSelector} from '~redux/index';

import {hideNotification, showNotification} from '~components/common/Notification';
import {UserRole} from 'src/contexts/AbilitiesContext';

/**
 * A debounced version of useEffect.
 *
 * @param effect The effect to debounce
 * @param delay delay in milliseconds
 * @param deps dependency array for the effect
 */
export function useDebouncedEffect(effect: EffectCallback, delay: number, deps?: DependencyList): void {
  const isFirstRender = useIsFirstRender();
  useEffect(() => {
    if (isFirstRender) {
      return;
    }
    const timeout = setTimeout(effect, delay);
    return () => {
      clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [delay, ...(deps || [])]);
}

/**
 * A debounced version of useDispatch. For arguments see {@link createDebouncer}
 *
 * @param delay The delay to debounce the dispatch by
 * @param leadingEdge Whether to dispatch immediately on the first call
 * @returns A debounced version of dispatch
 */
export function useDebouncedDispatch(delay: number, leadingEdge?: boolean): (action: any) => void {
  const dispatch = useAppDispatch();
  const debouncedDispatch = useMemo(() => {
    const debouncer = createDebouncer(delay, leadingEdge);
    return debouncer(dispatch);
  }, [delay, dispatch, leadingEdge]);

  return debouncedDispatch;
}

type useMenuHandlerReturnType<T> = {
  anchorEl: HTMLElement | null;
  isOpen: boolean;
  handleClick: (event: React.MouseEvent<T>) => void;
  handleClose: () => void;
};

/**
 * Provides state and callbacks for MUI Menu handling.
 * Based on this MUI Menu demo: https://mui.com/material-ui/react-menu/#positioned-menu)
 *
 * @returns `{anchorEl, isOpen, handleClick, handleClose}`
 */
export function usePopupHandler<T extends HTMLElement>(customOnClick?: () => void): useMenuHandlerReturnType<T> {
  const [anchorEl, setAnchorEl] = useState<null | T>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const handleClick = (event: React.MouseEvent<T>) => {
    setAnchorEl(event.currentTarget);
    setIsOpen(true);
    customOnClick?.();
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  return {anchorEl, isOpen, handleClick, handleClose};
}

type UseIsAdminOptions = {
  validateEmail?: boolean;
};

export function useIsAdmin(options?: UseIsAdminOptions): boolean {
  const {validateEmail = true} = options || {};
  const currentProject = useCurrentProjectQuery();
  const currentUser = useAppSelector(selectUser);
  const userRole = currentProject?.data?.users.find((user) => user.email === currentUser?.email)?.role;
  return userRole === UserRole.ADMIN || (validateEmail && isInternalEmail(currentUser?.email));
}

export function useIsTrainer(): boolean {
  const currentProject = useCurrentProjectQuery();
  const currentUser = useAppSelector(selectUser);
  const userRole = currentProject?.data?.users.find((user) => user.email === currentUser?.email)?.role;
  return userRole === UserRole.TRAINER;
}

export function useIsViewer(): boolean {
  const currentProject = useCurrentProjectQuery();
  const currentUser = useAppSelector(selectUser);
  const userRole = currentProject?.data?.users.find((user) => user.email === currentUser?.email)?.role;
  return userRole === UserRole.VIEWER;
}

export function useUserList(): UserWithId[] {
  const currentProject = useCurrentProjectQuery();
  const currentUser = useAppSelector(selectUser);

  const userEmail = currentUser?.email;

  const filteredUserList = useMemo(() => {
    const users = currentProject.data?.users || [];
    return [...users].filter((user) => isInternalEmail(userEmail) || !isInternalEmail(user.email));
  }, [currentProject.data?.users, userEmail]);

  return filteredUserList;
}
export function useWorkInProgressNotification() {
  useEffect(() => {
    if (isProductionEnv()) {
      return;
    }
    const wipNotification = showNotification({
      id: 'wip-notification',
      title: 'Work In Progress',
      message: 'This page is unfinished, it is not visible in production.',
      duration: Infinity,
      severity: 'warning',
    });
    return () => hideNotification(wipNotification);
  }, []);
}

export function usePerspectiveName(perspectiveId?: number | null) {
  const perspectives = useAppSelector(selectPerspectives);

  if (perspectiveId === null) {
    return null;
  }

  const perspective = perspectives.find((perspective) => perspective.id === perspectiveId);
  return perspective?.name || perspectiveId?.toString();
}

// Stable empty fallback so consumers can safely use the returned list as a hook dependency.
const NO_DEVICES: Device[] = [];

/**
 * The current project's devices. Keyed on the project via `useCurrentProjectQuery`, so it never
 * serves another project's devices — unlike the filters slice, which is not project-scoped and
 * keeps the previous project's list until some mounted component refetches the filters.
 */
export function useProjectDevices(): Device[] {
  const currentProject = useCurrentProjectQuery();

  return currentProject.data?.devices ?? NO_DEVICES;
}

/**
 * The alias of the given device, or `undefined` when it has none (or is not part of the project).
 * Callers can use that distinction to decide whether to show the raw device id instead.
 */
export function useDeviceAlias(deviceId?: string | null): string | undefined {
  const devices = useProjectDevices();

  return deviceId ? getDeviceAlias(deviceId, devices) : undefined;
}

export function useHotkeysWhenDialogsAreClosed(...args: Parameters<typeof useHotkeys>) {
  const [keys, callback, optionsOrDeps1, optionsOrDeps2] = args;
  const options = typeof optionsOrDeps1 === 'object' ? optionsOrDeps1 : optionsOrDeps2;
  const deps = typeof optionsOrDeps1 === 'object' ? optionsOrDeps2 : optionsOrDeps1;
  const [activeConfusionMatrixCellId] = useQueryParam(confusionMatrixCellIdQueryParam);
  const [evaluationCellPredictedClass] = useQueryParam(evaluationCellPredictedClassQueryParam);
  const [keypointFinetune] = useQueryParam(keypointFinetuneQueryParam);
  const isUnstableGallery = useIsUnstableGallery();

  // Wrap the useHotkeys callback in a function that checks if any dialogs are open
  // and only calls the callback if not.
  const callbackWithDialogCheck = useMemo(
    (): HotkeyCallback =>
      (...args: Parameters<HotkeyCallback>) => {
        const isImageBrowserDialog =
          !!activeConfusionMatrixCellId || !!evaluationCellPredictedClass || !!keypointFinetune || isUnstableGallery;
        if (!isImageBrowserDialog && hasOpenedDialogs()) {
          return;
        }
        callback(...args);
      },
    [activeConfusionMatrixCellId, evaluationCellPredictedClass, keypointFinetune, isUnstableGallery, callback],
  );

  return useHotkeys(keys, callbackWithDialogCheck, options, deps);
}

/*
 * A modified version of useElementSize from usehooks-ts that uses ResizeObserver. In comparison to
 * the original version, this adapts to changes in the element's size, not just the window's size.
 * @see https://github.com/juliencrn/usehooks-ts/issues/236#issuecomment-1291001854
 */
export const useElementSize = <T extends HTMLElement = HTMLDivElement>(): [RefObject<T>, Dimensions] => {
  const ref = useRef<T>(null);
  const [size, setSize] = useState<Dimensions>([0, 0]);

  useLayoutEffect(() => {
    const element = ref.current;
    const updateSize = (element: Element | null) => {
      const {width, height} = element?.getBoundingClientRect() ?? {
        width: 0,
        height: 0,
      };
      setSize([width, height]);
    };

    updateSize(element);

    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        updateSize(entry.target);
      }
    });

    if (element) {
      resizeObserver.observe(element);
    }

    return () => {
      if (element) {
        resizeObserver.unobserve(element);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref.current]);

  return [ref, size];
};

function truncateUrl(url: string) {
  const segments = url.split('/');

  // remove the last segment
  segments.pop();

  return segments.join('/');
}

export const useGoBack = () => {
  const router = useRouter();
  const navigate = useTanStackNavigate();
  const currentPath = router.state.location.pathname;
  const parentRoute = NAVIGATION_MAP[currentPath];
  const [similaritySearch] = useQueryParam(similaritySearchQueryParam);
  const [returnPathQueryValue] = useQueryParam(returnPathQueryParam);

  const returnPath =
    typeof returnPathQueryValue === 'string'
      ? returnPathQueryValue
      : Array.isArray(returnPathQueryValue)
        ? (returnPathQueryValue.find((value) => typeof value === 'string') ?? undefined)
        : undefined;

  // Get current search params to preserve them when navigating to parent route
  const currentSearchParams = useMemo(() => {
    const currentLocation = router.state.location;
    if (!currentLocation?.href) {
      return {};
    }
    const {query} = queryString.parseUrl(currentLocation.href, queryStringParseOptions);
    return query;
  }, [router.state.location]);

  return useCallback(() => {
    if (returnPath) {
      const {[returnPathQueryParam]: _returnPath, ...searchWithoutReturnPath} = currentSearchParams as Record<
        string,
        unknown
      >;
      navigate({to: returnPath as any, search: searchWithoutReturnPath as any});
    } else if (currentPath === Route.modelFinetuneImage) {
      // Navigate to parent route by removing /image from current path
      const parentPath = currentPath.replace('/image', '');
      navigate({to: parentPath as any, search: currentSearchParams as any});
    } else if (currentPath === Route.simsearchImage) {
      const simsearchRoute = getRoute(Route.simsearch, {
        query: {similaritySearch: similaritySearch as string, orderBy: ImageOrderBy.PROBABILITY_DESC},
      });
      navigate({to: simsearchRoute as any});
    } else if (parentRoute) {
      // Strip curation-specific params when leaving pipeline results pages
      const {
        curationPipelineTag,
        [viewPipelineIdQueryParam]: _viewPipelineId,
        orderBy: _orderBy,
        ...cleanedParams
      } = currentSearchParams as Record<string, unknown>;
      const searchParams = curationPipelineTag ? cleanedParams : currentSearchParams;
      navigate({to: parentRoute as any, search: searchParams as any});
    } else {
      navigate({to: truncateUrl(currentPath) as any, search: currentSearchParams as any});
    }
  }, [parentRoute, returnPath, similaritySearch, navigate, currentPath, currentSearchParams]);
};
